"use client"

import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { TabsContent } from "@/components/ui/tabs"

import {
  AI_INSPECTION_STOPS,
} from "./config"
import { SliderWithStops } from "./slider-with-stops"

const INSPECTION_STOP_LABELS: Record<number, string> = {
  3: "3 (short)",
  15: "15 (medium, typical Amazon)",
  30: "30 (long)",
}

const RETRY_STOP_VALUES = [0, 10, 20]

type ComputerPanelProps = {
  inspectionIndex: number
  onInspectionIndexChange: (index: number) => void
  retryPercent: number
  onRetryPercentChange: (percent: number) => void
}

export function ComputerPanel({
  inspectionIndex,
  onInspectionIndexChange,
  retryPercent,
  onRetryPercentChange,
}: ComputerPanelProps) {
  const retryIndex = Math.max(RETRY_STOP_VALUES.indexOf(retryPercent), 0)

  return (
    <TabsContent value="computer">
      <Card>
        <CardHeader>
          <CardTitle>Computer Use</CardTitle>
          <CardDescription>
            Estimate agent costs for shopping, support, or back-office workflows (pricing shown per 1,000 flows).
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="space-y-2">
            <SliderWithStops
              id="ai-inspections"
              label="AI inspections per workflow"
              values={AI_INSPECTION_STOPS}
              valueIndex={inspectionIndex}
              onIndexChange={onInspectionIndexChange}
              formatStopLabel={(stop) => INSPECTION_STOP_LABELS[stop] ?? `${stop}`}
              fullWidth
            />
          </div>
          <Field>
            <FieldLabel htmlFor="retry-rate" id="retry-rate-label">
              Retry rate
            </FieldLabel>
            <FieldDescription>
              Reserve capacity for workflows that need to be retried.
            </FieldDescription>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between text-sm">
                <span>Retry allowance</span>
                <span>{retryPercent}%</span>
              </div>
              <SliderWithStops
                id="retry-rate"
                values={RETRY_STOP_VALUES}
                valueIndex={retryIndex}
                onIndexChange={(index) =>
                  onRetryPercentChange(RETRY_STOP_VALUES[index] ?? RETRY_STOP_VALUES[0])
                }
                formatStopLabel={(value) => `${value}%`}
                showHeader={false}
                ariaLabelledBy="retry-rate-label"
                fullWidth
              />
            </div>
          </Field>
        </CardContent>
      </Card>
    </TabsContent>
  )
}
